import React, { useEffect, useState, useRef } from 'react';
import { Unity, useUnityContext } from "react-unity-webgl";
import styles from '../../static/styles/AimTrain.module.css';
import MessagePopup from '../../components/MessagePopup';
import loadingImg from '../../static/img/MadeByESL/loadingAimTrain.jpg'
import { useNavigate, useLocation } from 'react-router-dom';
import { submitGameExit } from '../../api/games';
import { eslTourMatchResult } from '../../api/tournaments';

const AimTrain = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [showRotateMessage, setShowRotateMessage] = useState(false);
  const [contextError, setContextError] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const gameContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const aimTrainState = location.state;
  const isTournamentMode = aimTrainState?.tournamentMode;

  useEffect(() => {
    // For tournament mode, we need different state properties
    if (isTournamentMode) {
      if (!aimTrainState?.roomId || !aimTrainState?.tournamentId) {
        navigate('/tournaments', { replace: true });
      }
    } else {
      // For single player mode, check original properties
      if (
        !aimTrainState ||
        !aimTrainState.game_unique_id ||
        !aimTrainState.game_name ||
        !aimTrainState.game_level ||
        !aimTrainState.level_credits
      ) {
        navigate('/daily-gaming-tasks', { replace: true });
      }
    }
  }, [aimTrainState, navigate, isTournamentMode]);

  const { 
    unityProvider, 
    isLoaded, 
    sendMessage, 
    requestFullscreen, 
    addEventListener,
    removeEventListener,
    unload,
    loadingProgression
  } = useUnityContext({
    loaderUrl: "AimTrain/Build/AimTrain.loader.js",
    dataUrl: "AimTrain/Build/AimTrain.data.unityweb",
    frameworkUrl: "AimTrain/Build/AimTrain.framework.js.unityweb",
    codeUrl: "AimTrain/Build/AimTrain.wasm.unityweb",
    webglContextAttributes: {
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
      failIfMajorPerformanceCaveat: false,
      majorVersion: 1, 
      minorVersion: 0
    }
  });

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    const handleError = (message) => {
      setContextError(true);
    };

    addEventListener("error", handleError);
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && contextError) {
        window.location.reload();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      removeEventListener("error", handleError);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unload();
    };
  }, [addEventListener, removeEventListener, unload, contextError]);

  useEffect(() => {
    if (isLoaded && aimTrainState) {
      setTimeout(() => {
        try {
          if (isTournamentMode) {
            // Send room data for tournament mode
            const msisdn = sessionStorage.getItem('msisdn');
            sendMessage(
              "GameManager",
              "SetRoomData",
              JSON.stringify({
                roomName: aimTrainState.roomId,
                playerUniqueId: msisdn,
                playerName: msisdn,
                isMobile: isMobile
              })
            );
          } else if (aimTrainState.game_unique_id) {
            // Send game level data for single player mode
            sendMessage(
              "GameManager",
              "SetGameLevel",
              JSON.stringify({
                game_unique_id: aimTrainState.game_unique_id,
                game_name: aimTrainState.game_name,
                game_level: aimTrainState.game_level,
                level_credits: aimTrainState.level_credits,
                isMobile: isMobile,
                roomName: sessionStorage.getItem('msisdn')
              })
            );
          }
        } catch (e) {}
      }, 500);
    }
  }, [isLoaded, sendMessage, aimTrainState, isMobile, isTournamentMode]);

  useEffect(() => {
    document.title = 'AimTrain | Ufone ESG';
  })

  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeNow = window.innerWidth > window.innerHeight;
      setIsLandscape(isLandscapeNow);
      setShowRotateMessage(isMobile && !isLandscapeNow);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, [isMobile]);

  useEffect(() => {
    let fullscreenAttemptTimeout;

    const lockOrientation = async () => {
      try {
        if (window.screen && window.screen.orientation) {
          await window.screen.orientation.lock('landscape');
        }
      } catch (error) {}
    };

    const enterFullscreen = async () => {
      try {
        if (isLoaded && loadingProgression === 1) {
          requestFullscreen(true);
          setIsFullscreen(true);
        } 
        else if (gameContainerRef.current && gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {}
    };

    const hideAddressBar = () => {
      if ('standalone' in window.navigator && !window.navigator.standalone) {
        setTimeout(() => {
          window.scrollTo(0, 1);
        }, 100);
      }
    };

    const handleOrientationAndUI = async () => {
      clearTimeout(fullscreenAttemptTimeout);
      if (isLoaded && isMobile) {
        fullscreenAttemptTimeout = setTimeout(async () => {
          await lockOrientation();
          hideAddressBar();
          await enterFullscreen();
        }, 1000);
      }
    };

    if (isLoaded) {
      handleOrientationAndUI();
    }

    const handleUserInteraction = () => {
      if (isLoaded && isMobile && !isFullscreen) {
        enterFullscreen();
        hideAddressBar();
      }
    };

    document.addEventListener('touchend', handleUserInteraction, false);
    document.addEventListener('click', handleUserInteraction, false);

    return () => {
      clearTimeout(fullscreenAttemptTimeout);
      document.removeEventListener('touchend', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
    };
  }, [isLoaded, requestFullscreen, isMobile, isFullscreen, loadingProgression]);

  useEffect(() => {
    function handleLevelPassed(levelNumber, finalScore) {
      requestFullscreen(false);
      if (aimTrainState && !isTournamentMode) {
        submitGameExit({
          game_unique_id: aimTrainState.game_unique_id,
          game_name: aimTrainState.game_name,
          game_level: aimTrainState.game_level,
          level_credits: aimTrainState.level_credits,
          is_passed: "1"
        });
      }
      setPopupMessage(`Level ${levelNumber} completed!`);
      setPopupOpen(true);
    }

    function handleLevelFailed(levelNumber, failReason) {
      requestFullscreen(false);
      if (aimTrainState && !isTournamentMode) {
        submitGameExit({
          game_unique_id: aimTrainState.game_unique_id,
          game_name: aimTrainState.game_name,
          game_level: aimTrainState.game_level,
          level_credits: aimTrainState.level_credits,
          is_passed: "0"
        });
      }
      setPopupMessage(`Level ${levelNumber} failed! Reason: ${failReason}`);
      setPopupOpen(true);
    }

    // Tournament mode callbacks
    function handleGameEnd(result) {
      requestFullscreen(false);
      const [outcome, playerId] = result.split('|');
      //console.log(`AimTrain tournament ended: ${outcome} for player ${playerId}`);

      if (isTournamentMode && aimTrainState?.tournamentId && aimTrainState?.roomId) {
        const resultFlag = outcome === 'victory' ? 'winner' : 'loser';
        eslTourMatchResult(aimTrainState.tournamentId, aimTrainState.roomId, resultFlag)
          .then(response => {
            //console.log('Tournament result submitted:', response);
          })
          .catch(error => {
            //console.error('Failed to submit tournament result:', error);
          });
      }

      if (outcome === 'victory') {
        setPopupMessage('Victory! You won the tournament match!');
      } else {
        setPopupMessage('Defeat! Better luck next time!');
      }
      setPopupOpen(true);
    }

    function handleWaitingForOpponent() {
      setPopupMessage('Waiting for opponent to join...');
      setPopupOpen(true);
    }

    function handleGameStart() {
      setPopupMessage('Match started! Show your aim skills!');
      setPopupOpen(true);
    }

    // Set up callbacks based on mode
    if (isTournamentMode) {
      window.onGameEnd = handleGameEnd;
      window.onWaitingForOpponent = handleWaitingForOpponent;
      window.onGameStart = handleGameStart;
    } else {
      window.onLevelPassed = handleLevelPassed;
      window.onLevelFailed = handleLevelFailed;
    }

    return () => {
      if (window.onLevelPassed === handleLevelPassed) window.onLevelPassed = undefined;
      if (window.onLevelFailed === handleLevelFailed) window.onLevelFailed = undefined;
      if (window.onGameEnd === handleGameEnd) window.onGameEnd = undefined;
      if (window.onWaitingForOpponent === handleWaitingForOpponent) window.onWaitingForOpponent = undefined;
      if (window.onGameStart === handleGameStart) window.onGameStart = undefined;
    };
  }, [aimTrainState, requestFullscreen, isTournamentMode]);

  const handlePopupClose = () => {
    setPopupOpen(false);
    unload();
    if (isTournamentMode) {
      navigate('/tournaments');
    } else {
      navigate('/daily-gaming-tasks');
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const TOTAL_MB = 65.3;
  const loadedMB = Math.max(0, Math.min(TOTAL_MB, Math.round((loadingProgression || 0) * TOTAL_MB * 10) / 10));

  const isPortraitMobile = isMobile && window.innerHeight > window.innerWidth;

  useEffect(() => {
    if (isMobile && !isPortraitMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        if (gameContainerRef.current) {
          gameContainerRef.current.focus?.();
        }
      }, 100);
    }
  }, [isMobile, isPortraitMobile]);

  return (
    <div
      ref={gameContainerRef}
      className={`${styles.gameContainer} ${isMobile && !isLandscape ? styles.portrait : ''}`}
    >
      {!isPortraitMobile && !contextError && (
        <div
          className={styles.unityWrapper}
          style={{
            visibility: isLoaded && (!isMobile || isLandscape || !showRotateMessage) ? 'visible' : 'hidden'
          }}
        >
          <Unity 
            unityProvider={unityProvider} 
            style={{ width: '100%', height: '100%' }}
            devicePixelRatio={window.devicePixelRatio}
          />
        </div>
      )}

      {contextError && (
        <div className={styles.errorOverlay}>
          <h2 className={styles.errorTitle}>Game Error</h2>
          <p className={styles.errorMessage}>
            There was an error loading the game. This may be due to WebGL compatibility issues.
          </p>
          <button className={styles.reloadButton} onClick={handleReload}>
            Reload Game
          </button>
        </div>
      )}

      {showRotateMessage && !contextError && (
        <div className={styles.rotateOverlay}>
          <div className={styles.rotateIcon}>
            <svg viewBox="0 0 100 100" width="100%" height="100%">
              <rect x="20" y="40" width="60" height="30" rx="5" stroke="white" strokeWidth="4" fill="none"/>
              <circle cx="50" cy="55" r="5" stroke="white" strokeWidth="2" fill="none"/>
            </svg>
          </div>
          <h2 className={styles.rotateText}>
            Please rotate your device to landscape mode
          </h2>
        </div>
      )}

      {!isLoaded && !contextError && !isPortraitMobile && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingBox}>
            <img
              src={loadingImg}
              alt="Loading AimTrain"
              className={styles.loadingImgBg}
              draggable={false}
            />
            <div className={styles.loadingProgressBarContainer}>
              <div
                className={styles.loadingProgressBar}
                style={{ width: `${Math.round((loadingProgression || 0) * 100)}%` }}
              />
            </div>
            <div className={styles.loadingText}>
              Loading game... {Math.round((loadingProgression || 0) * 100)}%
            </div>
            <div className={styles.loadingStats}>
              {loadedMB} MB / {TOTAL_MB} MB
            </div>
          </div>
        </div>
      )}

      {!isPortraitMobile && (
        <MessagePopup
          isOpen={popupOpen}
          message={popupMessage}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
};

export default AimTrain;